import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { FieldSuggestion } from '../../types/pipelineConfig';

interface FieldPathInputProps {
  value: string;
  onChange: (value: string) => void;
  suggestions: FieldSuggestion[];
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}

export function FieldPathInput({
  value,
  onChange,
  suggestions,
  placeholder = 'e.g., data.essentials.alertId',
  disabled = false,
  className,
}: FieldPathInputProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  const filteredSuggestions = useMemo(() => {
    if (!value.trim()) return suggestions;
    const query = value.toLowerCase();
    return suggestions.filter(
      (s) =>
        s.path.toLowerCase().includes(query) ||
        s.label.toLowerCase().includes(query) ||
        (s.category?.toLowerCase().includes(query) ?? false),
    );
  }, [value, suggestions]);

  const groupedSuggestions = useMemo(() => {
    const grouped = new Map<string, FieldSuggestion[]>();
    for (const suggestion of filteredSuggestions) {
      const category = suggestion.category ?? 'Other';
      const existing = grouped.get(category) ?? [];
      existing.push(suggestion);
      grouped.set(category, existing);
    }
    return [...grouped.entries()];
  }, [filteredSuggestions]);

  const flatSuggestions = useMemo(
    () => groupedSuggestions.flatMap(([, items]) => items),
    [groupedSuggestions],
  );

  const groupedSuggestionsWithIndex = useMemo(() => {
    let optionIndex = 0;
    return groupedSuggestions.map(([category, categorySuggestions]) => ({
      category,
      items: categorySuggestions.map((suggestion) => ({
        suggestion,
        optionIndex: optionIndex++,
      })),
    }));
  }, [groupedSuggestions]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Scroll highlighted item into view
  useEffect(() => {
    if (highlightedIndex >= 0 && listRef.current) {
      const item = listRef.current.querySelector<HTMLElement>(
        `[data-option-index="${highlightedIndex}"]`,
      );
      item?.scrollIntoView({ block: 'nearest' });
    }
  }, [highlightedIndex]);

  const selectSuggestion = useCallback(
    (path: string) => {
      onChange(path);
      setIsOpen(false);
      setHighlightedIndex(-1);
      inputRef.current?.focus();
    },
    [onChange],
  );

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      setIsOpen(false);
      setHighlightedIndex(-1);
      return;
    }

    if (!isOpen || flatSuggestions.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIndex((prev) =>
        prev < flatSuggestions.length - 1 ? prev + 1 : 0,
      );
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIndex((prev) =>
        prev > 0 ? prev - 1 : flatSuggestions.length - 1,
      );
    } else if (e.key === 'Enter' && highlightedIndex >= 0) {
      e.preventDefault();
      selectSuggestion(flatSuggestions[highlightedIndex].path);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(e.target.value);
    setIsOpen(true);
    setHighlightedIndex(-1);
  };

  const showDropdown = isOpen && flatSuggestions.length > 0;

  return (
    <div ref={containerRef} className={`relative ${className ?? ''}`}>
      <input
        ref={inputRef}
        type="text"
        className="input"
        value={value}
        onChange={handleInputChange}
        onFocus={() => setIsOpen(true)}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        disabled={disabled}
        autoComplete="off"
        role="combobox"
        aria-expanded={showDropdown}
        aria-autocomplete="list"
        aria-controls="field-path-listbox"
      />

      {showDropdown && (
        <ul
          ref={listRef}
          id="field-path-listbox"
          role="listbox"
          className="absolute z-20 mt-1 w-full max-h-60 overflow-auto rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 shadow-lg"
        >
          {groupedSuggestionsWithIndex.map(({ category, items }) => (
              <Fragment key={`group-${category}`}>
                <li
                  key={`header-${category}`}
                  className="px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-900/60 border-y border-gray-100 dark:border-gray-700"
                >
                  {category}
                </li>
                {items.map(({ suggestion, optionIndex: currentIndex }) => {
                  const isHighlighted = currentIndex === highlightedIndex;

                  return (
                    <li
                      key={`${category}:${suggestion.path}:${currentIndex}`}
                      data-option-index={currentIndex}
                      role="option"
                      aria-selected={isHighlighted}
                      className={`cursor-pointer px-3 py-2 text-sm ${
                        isHighlighted
                          ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200'
                          : 'text-gray-900 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-700'
                      }`}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        selectSuggestion(suggestion.path);
                      }}
                      onMouseEnter={() => setHighlightedIndex(currentIndex)}
                    >
                      <div className="font-medium font-mono text-xs">{suggestion.path}</div>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          {suggestion.label}
                        </span>
                        {suggestion.description && (
                          <span className="text-xs text-gray-400 dark:text-gray-500">
                            — {suggestion.description}
                          </span>
                        )}
                      </div>
                    </li>
                  );
                })}
              </Fragment>
            ))}
        </ul>
      )}
    </div>
  );
}
